import React from 'react'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  UserManagementFilterConstants,
  getToolFilters,
  getStatusFilters,
} from '../../store/userManagement/userManagementSlice'
import { Styles } from '../../types'
import UserManagementCheckBoxesFilter from './UserManagementCheckBoxesFilter'
import { FilterOptionsResponse } from '../../store/userManagement/userManagementSearchService'

const styles: Styles = {
  container: {
    flexDirection: 'column',
    padding: 0,
  },
}

interface UserManagementFiltersProps {
  filterData?: FilterOptionsResponse
  isFilterError: boolean
  isFilterSuccess: boolean
  clearCategoryFilter: (category: string) => void
  changeFilterAndSearch: (filterOption: string, value: string) => void
}

/**
 * UserManagementFilters orchestrates all filter panels for the User Management interface.
 * This component serves as the main filter container that renders tool filters and status
 * filters with clear functionality, following the established InvoicesFilters pattern.
 *
 * Key Features:
 * - Renders multi-select tool filters and status filters using UserManagementCheckBoxesFilter
 * - Handles API response data from GET /api/v1/clientadmin/filters endpoint
 * - Maintains filter functionality even when no user data exists (empty state)
 * - Provides error handling with internationalized error messages
 * - Integrates with Redux state management for filter persistence
 * - Supports clear functionality for individual filter categories
 * - Uses Material-UI Box component for consistent styling and layout
 *
 * Filter Categories Supported:
 * - Tools: Multi-select filter for user management tools (Enterprise Alumni Stage,
 *   FamilySight, myRSM, RevNsight Analytics, Risk Monitor, TaxCaddy, TeamsDocs, Trade)
 * - Status: Multi-select filter for user status (Active, Provisioned, Deprovisioned)
 *
 * @param filterData - API response containing filter options dictionary from backend
 * @param isFilterError - Flag indicating if filter data fetch encountered an error
 * @param isFilterSuccess - Flag indicating successful filter data retrieval
 * @param clearCategoryFilter - Callback to clear all filters within a specific category
 * @param changeFilterAndSearch - Callback to handle filter state changes and trigger search
 */
const UserManagementFilters = ({
  filterData = undefined,
  isFilterError,
  isFilterSuccess,
  clearCategoryFilter,
  changeFilterAndSearch,
}: UserManagementFiltersProps) => {
  const { t } = useTranslation()

  // Get current selected filters from Redux state
  const toolFilters = useSelector(getToolFilters)
  const statusFilters = useSelector(getStatusFilters)

  // Handle error state by displaying user-friendly error message
  if (isFilterError) {
    return (
      <Box sx={styles.container}>{t('UserManagement.ErrorGettingFilters')}</Box>
    )
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isFilterSuccess && (
        <Box sx={styles.container} className="filters">
          {Object.keys(filterData ?? []).map((filterCategory: string) => {
            switch (filterCategory) {
              case UserManagementFilterConstants.tools:
                return (
                  <UserManagementCheckBoxesFilter
                    key={filterCategory}
                    filterCategory={filterCategory}
                    filterOptions={
                      filterData ? (filterData[filterCategory] ?? []) : []
                    }
                    clearCategoryFilter={clearCategoryFilter}
                    changeFilterAndSearch={changeFilterAndSearch}
                    visibleItemsCount={5}
                    selectedFilters={toolFilters}
                  />
                )
              case UserManagementFilterConstants.status:
                return (
                  <UserManagementCheckBoxesFilter
                    key={filterCategory}
                    filterCategory={filterCategory}
                    filterOptions={
                      filterData ? (filterData[filterCategory] ?? []) : []
                    }
                    clearCategoryFilter={clearCategoryFilter}
                    changeFilterAndSearch={changeFilterAndSearch}
                    visibleItemsCount={3}
                    selectedFilters={statusFilters}
                  />
                )
              default:
                return null
            }
          })}
        </Box>
      )}
    </>
  )
}

export default UserManagementFilters
